import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ApplyPage = () => {
  const [showProvinceMap, setShowProvinceMap] = useState(false);

  // Google Ads conversion tracking helper
  const trackConversion = (eventName) => {
    if (typeof window.gtag === 'function') {
      // Google Ads conversion tracking with labels and values
      if (eventName === 'business_use_application') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/ihQUCJ6gr-MbEM3m36Mp',
          'value': 400.0,
          'currency': 'CAD'
        });
      } else if (eventName === 'personal_use_application') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/wWXOCKGgr-MbEM3m36Mp',
          'value': 100.0,
          'currency': 'CAD'
        });
      } else if (eventName === 'contact_form_submission') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11080823629/-m0aCKSgr-MbEM3m36Mp',
          'value': 100.0,
          'currency': 'CAD'
        });
      }
      // Also send to GA4 for analytics
      window.gtag('event', eventName);
    }
  };

  const handleProvinceClick = (province) => {
    const eastProvinces = ['ontario', 'quebec', 'new-brunswick', 'nova-scotia', 'pei', 'newfoundland', 'nunavut', 'northwest-territories', 'yukon'];
    const westProvinces = ['british-columbia', 'alberta', 'saskatchewan', 'manitoba'];
    
    // Track Business Use application click
    trackConversion('business_use_application');
    
    if (eastProvinces.includes(province)) {
      window.open('https://app.useanvil.com/weld/equipease-lease-co/creditapplicationAFS', '_blank');
    } else if (westProvinces.includes(province)) {
      window.open('https://app.useanvil.com/weld/equipease-lease-co/credit-application-form-heavy-equipment', '_blank');
    }
    setShowProvinceMap(false);
  };

  const handlePersonalUseClick = () => {
    // Track Personal Use application click
    trackConversion('personal_use_application');
    window.open('https://share.hsforms.com/1ebxMZSTqT8uHvOcu3XLh5Qbne2o', '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Apply Now - Equipment Leasing & Financing Application | EquipEASE</title>
        <meta name="description" content="Apply for equipment leasing & financing with EquipEASE. Fast approvals, competitive rates, and flexible terms for Canadian businesses. Start your application today." />
        <link rel="canonical" href="https://equipease.ca/apply" />
      </Helmet>
      
      <Header />
      
      <section className="flex-1 py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#333333]">Apply for Equipment Financing</h1>
            <p className="text-xl text-[#4A4A4A]">
              Ready to get the equipment your business needs? Select your application type below to get started.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Business Use Card */}
            <div 
              onClick={() => setShowProvinceMap(true)}
              className="bg-gradient-to-br from-[#FF6600] to-[#FF9933] p-12 rounded-lg shadow-xl cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105 text-white"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold mb-4">Business Use?</h2>
                <p className="text-white/90 text-lg mb-4">Apply for business equipment financing</p>
                <ul className="text-white/80 text-sm text-left space-y-2 mt-6">
                  <li className="flex items-center"><span className="mr-2">✓</span> Construction Equipment</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Commercial Vehicles</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Medical & Dental Equipment</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Agricultural Machinery</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Manufacturing Equipment</li>
                </ul>
              </div>
            </div>

            {/* Personal Use Card */}
            <div 
              onClick={handlePersonalUseClick}
              className="bg-gradient-to-br from-[#4A4A4A] to-[#333333] p-12 rounded-lg shadow-xl cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105 text-white"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold mb-4">Personal Use?</h2>
                <p className="text-white/90 text-lg mb-4">Apply for personal equipment financing</p>
                <ul className="text-white/80 text-sm text-left space-y-2 mt-6">
                  <li className="flex items-center"><span className="mr-2">✓</span> RVs & Motorhomes</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Boats & Watercraft</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> ATVs & Powersports</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Trailers</li>
                  <li className="flex items-center"><span className="mr-2">✓</span> Recreational Equipment</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="max-w-4xl mx-auto mt-16 text-center">
            <h3 className="text-2xl font-bold text-[#333333] mb-4">Need Help Deciding?</h3>
            <p className="text-[#4A4A4A] mb-6">
              Not sure which application is right for you? Our financing specialists are here to help guide you through the process.
            </p>
            <a 
              href="tel:1-844-250-3273" 
              className="inline-flex items-center text-[#FF6600] hover:text-[#FF9933] font-semibold text-xl transition-colors"
            >
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call 1-844-250-EASE
            </a>
          </div>
        </div>
      </section>

      {/* Province Selection Modal */}
      {showProvinceMap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProvinceMap(false)}>
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-bold text-[#333333]">What Province Are You In?</h3>
              <button onClick={() => setShowProvinceMap(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: 'British Columbia', id: 'british-columbia' },
                { name: 'Alberta', id: 'alberta' },
                { name: 'Saskatchewan', id: 'saskatchewan' },
                { name: 'Manitoba', id: 'manitoba' },
                { name: 'Ontario', id: 'ontario' },
                { name: 'Quebec', id: 'quebec' },
                { name: 'New Brunswick', id: 'new-brunswick' },
                { name: 'Nova Scotia', id: 'nova-scotia' },
                { name: 'Prince Edward Island', id: 'pei' },
                { name: 'Newfoundland & Labrador', id: 'newfoundland' },
                { name: 'Yukon', id: 'yukon' },
                { name: 'Northwest Territories', id: 'northwest-territories' },
                { name: 'Nunavut', id: 'nunavut' }
              ].map((province) => (
                <button
                  key={province.id}
                  onClick={() => handleProvinceClick(province.id)}
                  className="p-4 bg-gray-100 hover:bg-[#FF6600] hover:text-white rounded-lg transition-colors text-left font-medium"
                >
                  {province.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ApplyPage;
